import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  FiCheck,
  FiX,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
  FiPieChart,
  FiDownload,
  FiShare2,
  FiRefreshCw,
  FiBookOpen,
  FiAward,
  FiTarget,
  FiEye,
  FiEyeOff,
  FiArrowLeft,
  FiArrowRight,
  FiActivity,
  FiZap
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import AIInsights from '../../components/common/AIInsights';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';
import '../../styles/test-results.css';

const TestResults = () => {
  const [results, setResults] = useState(null);
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAnswers, setShowAnswers] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const submissionId = location.state?.submissionId;
  
  const loadResults = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading test results for:', { testId, submissionId });
      
      // Load data with individual error handling
      const promises = [
        testAPI.getTestResults(testId, submissionId).catch(err => {
          console.error('❌ Failed to load test results:', err);
          throw new Error(`Failed to load test results: ${err.response?.data?.message || err.message}`);
        }),
        testAPI.getTest(testId).catch(err => {
          console.error('❌ Failed to load test data:', err);
          throw new Error(`Failed to load test data: ${err.response?.data?.message || err.message}`);
        }),
        testAPI.getTestQuestions(testId).catch(err => {
          console.error('❌ Failed to load test questions:', err);
          throw new Error(`Failed to load test questions: ${err.response?.data?.message || err.message}`);
        }),
        testAPI.getTestAnalytics(testId, submissionId).catch(err => {
          console.error('⚠️ Failed to load test analytics (non-critical):', err);
          return { data: null }; // Return null data for analytics as it's non-critical
        })
      ];
      
      const [resultsResponse, testResponse, questionsResponse, analyticsResponse] = await Promise.all(promises);
      
      // Extract data from API responses with better error handling
      const resultsData = resultsResponse.data?.data || resultsResponse.data;
      const testData = testResponse.data?.data || testResponse.data;
      const questionsData = questionsResponse.data?.data || questionsResponse.data;
      const analyticsData = analyticsResponse?.data?.data || analyticsResponse?.data || null;
      
      console.log('✅ Successfully loaded test data:', {
        results: !!resultsData,
        test: !!testData,
        questions: questionsData?.length || 0,
        analytics: !!analyticsData
      });
      
      // Validate critical data
      if (!resultsData) {
        throw new Error('Test results data is missing or invalid');
      }
      if (!testData) {
        throw new Error('Test data is missing or invalid');
      }
      if (!questionsData || !Array.isArray(questionsData)) {
        throw new Error('Test questions data is missing or invalid');
      }
      
      setResults(resultsData);
      setTest(testData);
      setQuestions(questionsData);
      setUserAnswers(resultsData.answers || {});
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('💥 Failed to load results:', err);
      const errorMessage = err.message || 'Failed to load test results. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [testId, submissionId, showError]);

  useEffect(() => {
    if (testId) {
      loadResults();
    }
  }, [testId, loadResults]);
  
  const downloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      const pdfBlob = await testAPI.downloadResultsPDF(testId, submissionId);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${test?.title || 'Test'}_Results.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Results downloaded successfully');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showError('Failed to download results');
    } finally {
      setDownloadingPDF(false);
    }
  };
  
  const shareResults = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${test?.title} Results`,
          text: `I scored ${results?.percentage}% on ${test?.title}!`,
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        showSuccess('Results link copied to clipboard');
      }
    } catch (error) {
      console.error('Failed to share results:', error);
      showError('Failed to share results');
    }
  };
  
  const retakeTest = () => {
    navigate(`/tests/${testId}/take`);
  };
  
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };
  
  const getScoreColor = (percentage) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 80) return 'info';
    if (percentage >= 70) return 'warning';
    return 'danger';
  };
  
  const isAnswerCorrect = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    const userAnswer = userAnswers[questionId];
    const correctOption = question?.options?.find(opt => opt.isCorrect);
    return userAnswer === correctOption?.id;
  };
  
  const getAnswerStatus = (questionId) => {
    if (!userAnswers.hasOwnProperty(questionId)) return 'unanswered';
    return isAnswerCorrect(questionId) ? 'correct' : 'incorrect';
  };
  
  if (loading) {
    return (
      <div className="test-results-loading">
        <LoadingSpinner size="large" />
        <p>Loading results...</p>
      </div>
    );
  }
  
  if (error && !results) {
    return (
      <div className="test-results-error">
        <FiX size={48} />
        <h2>Unable to Load Results</h2>
        <p>{error}</p>
        <div className="error-actions">
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            Go to Dashboard
          </Button>
          <Button onClick={loadResults}>Try Again</Button>
        </div>
      </div>
    );
  }
  
  const currentQuestion = questions[currentQuestionIndex];
  const userAnswer = userAnswers[currentQuestion?.id];
  
  return (
    <div className="test-results">
      {/* Compact header */}
      <header className="results-header">
        <Button
          variant="text"
          size="small"
          onClick={() => navigate('/test-results')}
          icon={FiArrowLeft}
          className="back-link"
        >
          Back to results
        </Button>
        <div className="header-row">
          <div className="header-main">
            <h1 className="results-title">{test?.title}</h1>
            <p className="results-meta">
              {[test?.subject, test?.examType].filter(Boolean).join(' · ')}
              {results?.submittedAt && (
                <> · Submitted {new Date(results.submittedAt).toLocaleDateString()}</>
              )}
            </p>
          </div>
          <div className={`score-block ${getScoreColor(results?.percentage)}`}>
            <span className="score-number">{results?.percentage}%</span>
            <span className="score-caption">
              {results?.score}/{results?.totalPoints} · {results?.passed ? 'Passed' : 'Not passed'}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate('/test-results')} variant="outline" size="small">
            Done
          </Button>
          <Button onClick={downloadPDF} loading={downloadingPDF} variant="outline" size="small" icon={FiDownload}>
            PDF
          </Button>
          <Button onClick={shareResults} variant="outline" size="small" icon={FiShare2}>
            Share
          </Button>
          <Button onClick={retakeTest} variant="outline" size="small" icon={FiRefreshCw}>
            Retake
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="results-tabs">
        {[
          { id: 'overview', label: 'Overview', icon: FiBarChart2 },
          { id: 'questions', label: 'Questions', icon: FiBookOpen },
          { id: 'analytics', label: 'Analytics', icon: FiPieChart },
          { id: 'insights', label: 'Insights', icon: FiZap },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`tab-btn ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </nav>
      
      {/* Tab content */}
      <main className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-value">{results?.percentage}%</span>
                <span className="stat-label">Score</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatTime(results?.timeSpent)}</span>
                <span className="stat-label">Time</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{results?.correctAnswers}/{questions?.length}</span>
                <span className="stat-label">Correct</span>
              </div>
              {(results?.rank != null && results?.totalParticipants != null) && (
                <div className="stat-item">
                  <span className="stat-value">#{results.rank}</span>
                  <span className="stat-label">of {results.totalParticipants}</span>
                </div>
              )}
            </div>

            <div className="overview-grid">
              <section className="card performance-card">
                <h3 className="card-title">Breakdown</h3>
                <div className="performance-inner">
                  <div className="donut-wrap">
                    <svg viewBox="0 0 100 100" className="donut-svg">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--tr-track)" strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r="40"
                        fill="none"
                        stroke="var(--tr-correct)"
                        strokeWidth="10"
                        strokeDasharray={`${(results?.correctAnswers / (questions?.length || 1)) * 251.2} 251.2`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <span className="donut-value">{Math.round((results?.correctAnswers / (questions?.length || 1)) * 100)}%</span>
                  </div>
                  <div className="perf-legend">
                    <div className="perf-row"><span className="dot correct" /> Correct <strong>{results?.correctAnswers}</strong></div>
                    <div className="perf-row"><span className="dot incorrect" /> Incorrect <strong>{results?.incorrectAnswers}</strong></div>
                    <div className="perf-row"><span className="dot flagged" /> Flagged <strong>{results?.flaggedQuestions?.length || 0}</strong></div>
                  </div>
                </div>
              </section>

              {(results?.rank != null || analytics?.percentile != null || analytics?.classAverage != null) && (
                <section className="card">
                  <h3 className="card-title">Class</h3>
                  <div className="class-stats">
                    {results?.rank != null && <p><strong>#{results.rank}</strong> of {results.totalParticipants}</p>}
                    {analytics?.percentile != null && <p>{analytics.percentile}th percentile</p>}
                    {analytics?.classAverage != null && (
                      <p>Average {analytics.classAverage}% {results?.percentage > analytics.classAverage ? <FiTrendingUp className="icon-up" /> : <FiTrendingDown className="icon-down" />}</p>
                    )}
                  </div>
                </section>
              )}

              {analytics?.topicPerformance?.length > 0 && (
                <section className="card card-full">
                  <h3 className="card-title">By topic</h3>
                  <ul className="topic-list">
                    {analytics.topicPerformance.map((topic, i) => (
                      <li key={i} className="topic-row">
                        <div className="topic-info">
                          <span className="topic-name">{topic.topic}</span>
                          <span className="topic-score">{topic.correct}/{topic.total} · {topic.percentage}%</span>
                        </div>
                        <div className="topic-bar"><div className={`topic-fill ${getScoreColor(topic.percentage)}`} style={{ width: `${topic.percentage}%` }} /></div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'questions' && (
          <div className="questions-tab">
            <div className="questions-toolbar">
              <span className="toolbar-label">Question {currentQuestionIndex + 1} of {questions.length}</span>
              <Button variant="text" size="small" onClick={() => setShowAnswers(!showAnswers)} icon={showAnswers ? FiEyeOff : FiEye}>
                {showAnswers ? 'Hide answers' : 'Show answers'}
              </Button>
            </div>
            <div className="question-nav-grid">
              {questions.map((q, i) => {
                const status = getAnswerStatus(q.id);
                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`q-dot ${status} ${i === currentQuestionIndex ? 'current' : ''}`}
                    onClick={() => setCurrentQuestionIndex(i)}
                    title={`Question ${i + 1}`}
                  >
                    {i + 1}
                    {status === 'correct' && <FiCheck size={10} />}
                    {status === 'incorrect' && <FiX size={10} />}
                  </button>
                );
              })}
            </div>
            {currentQuestion && (
              <div className="question-review-card">
                <div className="question-review-nav">
                  <Button variant="text" size="small" onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))} disabled={currentQuestionIndex === 0} icon={FiArrowLeft}>Prev</Button>
                  <Button variant="text" size="small" onClick={() => setCurrentQuestionIndex(Math.min(questions.length - 1, currentQuestionIndex + 1))} disabled={currentQuestionIndex === questions.length - 1} icon={FiArrowRight} iconPosition="right">Next</Button>
                </div>
                <p className="question-text">{currentQuestion.question?.text || currentQuestion.question?.question || (typeof currentQuestion.question === 'string' ? currentQuestion.question : 'Question text not available')}</p>
                <ul className="options-list">
                  {currentQuestion.options.map((opt) => {
                    const isUser = userAnswer === opt.id;
                    const isCorrect = opt.isCorrect;
                    let rowClass = 'opt-row';
                    if (showAnswers) { if (isCorrect) rowClass += ' correct'; if (isUser && !isCorrect) rowClass += ' wrong'; }
                    else if (isUser) rowClass += ' selected';
                    return (
                      <li key={opt.id} className={rowClass}>
                        <span className="opt-indicator">{showAnswers && isCorrect && <FiCheck size={14} />}{showAnswers && isUser && !isCorrect && <FiX size={14} />}</span>
                        <span className="opt-text">{opt.text}</span>
                        {isUser && <span className="opt-badge">Yours</span>}
                      </li>
                    );
                  })}
                </ul>
                {showAnswers && currentQuestion.explanation && (
                  <div className="explanation-block">
                    <strong>Explanation:</strong> {typeof currentQuestion.explanation === 'string' ? currentQuestion.explanation : currentQuestion.explanation?.text || ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'analytics' && (
          <div className="analytics-tab">
            <section className="card">
              <h3 className="card-title">Analytics</h3>
              <p className="muted">Detailed analytics will appear here when available.</p>
            </section>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="insights-tab">
            {results?.aiInsights ? (
              <div className="insights-cards">
                {results.aiInsights.detailedInsight && (
                  <section className="card insight-card">
                    <h3 className="card-title"><FiZap size={16} /> Summary</h3>
                    <p className="insight-text">{results.aiInsights.detailedInsight}</p>
                  </section>
                )}
                <div className="insight-badges">
                  {results.aiInsights.chanceOfPassing && (
                    <div className="badge-row">
                      <span className="badge-label">Chance of passing</span>
                      <Badge variant={results.aiInsights.chanceOfPassing === 'High' ? 'success' : results.aiInsights.chanceOfPassing === 'Low' ? 'danger' : 'warning'}>{results.aiInsights.chanceOfPassing}</Badge>
                    </div>
                  )}
                  {results.aiInsights.readinessLevel && (
                    <div className="badge-row">
                      <span className="badge-label">Readiness</span>
                      <Badge variant={results.aiInsights.readinessLevel === 'exam_ready' ? 'success' : results.aiInsights.readinessLevel === 'not_ready' ? 'danger' : 'warning'}>
                        {results.aiInsights.readinessLevel.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  )}
                </div>
                {results.aiInsights.strengths?.length > 0 && (
                  <section className="card">
                    <h3 className="card-title"><FiTarget size={16} /> Strengths</h3>
                    <ul className="insight-list">{results.aiInsights.strengths.map((s, i) => <li key={i}><strong>{s.area || s.topic}</strong>: {s.description}</li>)}</ul>
                  </section>
                )}
                {results.aiInsights.weaknesses?.length > 0 && (
                  <section className="card">
                    <h3 className="card-title"><FiTrendingDown size={16} /> Areas to improve</h3>
                    <ul className="insight-list">
                      {results.aiInsights.weaknesses.map((w, i) => (
                        <li key={i}>
                          <><strong>{w.area || w.topic}</strong>: {w.description}
                          {w.recommendations?.length > 0 && <ul className="sublist">{w.recommendations.map((r, j) => <li key={j}>{r}</li>)}</ul>}</>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {results.aiInsights.aiRecommendations?.length > 0 && (
                  <section className="card">
                    <h3 className="card-title"><FiBookOpen size={16} /> Recommendations</h3>
                    <ul className="insight-list">{results.aiInsights.aiRecommendations.map((rec, i) => <li key={i}>{rec}</li>)}</ul>
                  </section>
                )}
                {results.aiInsights.studyPlan?.length > 0 && (
                  <section className="card">
                    <h3 className="card-title"><FiActivity size={16} /> Study plan</h3>
                    <ul className="insight-list">
                      {results.aiInsights.studyPlan.map((p, i) => (
                        <li key={i}><strong>{p.topic}</strong> ({p.priority}) — {p.estimatedStudyTime}h{p.resources?.length > 0 && ` — ${p.resources.join(', ')}`}</li>
                      ))}
                    </ul>
                  </section>
                )}
                {results.aiInsights.predictedExamScore && (results.aiInsights.predictedExamScore.waec != null || results.aiInsights.predictedExamScore.jamb != null || results.aiInsights.predictedExamScore.postUme != null) && (
                  <section className="card">
                    <h3 className="card-title"><FiAward size={16} /> Predicted scores</h3>
                    <div className="predicted-scores">
                      {results.aiInsights.predictedExamScore.waec != null && <span>WAEC {results.aiInsights.predictedExamScore.waec}%</span>}
                      {results.aiInsights.predictedExamScore.jamb != null && <span>JAMB {results.aiInsights.predictedExamScore.jamb}/400</span>}
                      {results.aiInsights.predictedExamScore.postUme != null && <span>Post-UME {results.aiInsights.predictedExamScore.postUme}%</span>}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <AIInsights testResults={results} testData={test} userAnswers={userAnswers} questions={questions} />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TestResults;